<template>
    <v-layout row wrap>
        <v-flex xs12>
            <v-alert type="info" :value="!selectedFile">
                <v-layout column>
                    <v-flex>
                        <span>Select a valid CSV file to import.</span>
                    </v-flex>
                </v-layout>
            </v-alert>
        </v-flex>
        <v-flex xs12>
            <v-layout row>
                <v-flex grow>
                    <v-text-field
                        label="File"
                        :value="fileName"
                        @input="removeFile"
                        placeholder="No file selected"
                        clearable
                        readonly
                        @click="addFile"
                        :loading="processingFile"
                        :disabled="performingImport">
                    </v-text-field>
                    <input
                        type="file"
                        style="display: none"
                        ref="newFile"
                        :accept="inputFileTypes"
                        @change="handleNewFile($event)"
                    >
                </v-flex>
                <v-flex shrink>
                    <v-btn :block="$vuetify.breakpoint.xs" color="primary" @click="addFile()" :loading="processingFile" :disabled="performingImport">
                        <span v-if="!selectedFile">Add File</span>
                        <span v-else>Replace File</span>
                    </v-btn>
                </v-flex>
            </v-layout>
        </v-flex>
        <v-flex xs12 v-if="selectedFile && showWarning">
            <v-alert type="warning" v-if="fileExtensionWarning" :value="true">
                <span>File is not of a recognised file type ({{ inputFileTypes }})</span>
            </v-alert>
        </v-flex>
        <v-flex xs12>
            <v-btn color="primary" :block="$vuetify.breakpoint.xs" @click="performImport()" :loading="performingImport" :disabled="!selectedFile">Import</v-btn>
        </v-flex>
        <v-flex xs12 v-if="!hasError && !hasSuccess">
            <v-alert type="info" :value="true">
                <v-layout column>
                    <v-flex>
                        <span>If certain master data is missing, you will be asked if you want to continue or abort the process. There may be errors which will also abort the process and inform you of necessary action.</span>
                    </v-flex>
                    <v-flex class="mt-1">
                         <span>Data will only be written to the system if all continues are confirmed and no errors are produced.</span>
                    </v-flex>
                </v-layout>
            </v-alert>
        </v-flex>
        <v-flex xs12 v-if="hasError">
            <v-alert type="error" :value="true">
                <v-layout column>
                    <v-flex>
                        <span>The import failed to complete due to error. No records were written to the system. Please resolve the issues and try again.</span>
                    </v-flex>
                </v-layout>
            </v-alert>
            <v-card v-if="errorMessages.length > 0" class="mb-1">
                <v-toolbar dark color="error" card @click="errorMessagesExpanded = !errorMessagesExpanded">
                    <span class="headline">Import Errors</span>
                    <v-spacer></v-spacer>
                    <v-toolbar-items>
                        <v-btn icon>
                            <v-icon v-if="!errorMessagesExpanded">expand_more</v-icon>
                            <v-icon v-if="errorMessagesExpanded">expand_less</v-icon>
                        </v-btn>
                    </v-toolbar-items>
                </v-toolbar>
                <v-card-text v-if="errorMessagesExpanded">
                    <v-alert type="error" :value="true" v-for="(errorMessage, index) in errorMessages" :key="`errorMesssage-${index}`">
                        <v-layout column>
                            <v-flex>
                                <span>{{ errorMessage.description }}</span>
                                <span v-if="errorMessage.detail && !errorMessage.showDetail" class="text--darken-1 ml-1 clickable" @click="showDetail(errorMessage)">(More)</span>
                            </v-flex>
                            <v-flex v-if="errorMessage.showDetail">
                                <span class="red--text text--lighten-3 stacktrace">{{ errorMessage.detail }}</span>
                            </v-flex>
                            <v-flex v-if="errorMessage.showDetail">
                                <span class="text--darken-1 clickable" @click="hideDetail(errorMessage)">(Less)</span>
                            </v-flex>
                        </v-layout>
                    </v-alert>
                </v-card-text>
            </v-card>
        </v-flex>
        <v-flex xs12 v-if="hasSuccess">
            <v-alert type="success" v-if="successMessage" :value="true">
                <span>{{ successMessage }}</span>
            </v-alert>
        </v-flex>
        <v-dialog :value="continueDialogVisible" transition="dialog-bottom-transition">
            <v-card>
                <v-card-text>
                    <span>{{ continueQuestion }}</span>
                </v-card-text>
                <v-card-actions>
                    <v-btn color="success" @click="performContinue">Continue</v-btn>
                    <v-btn color="error" @click="performAbort">Abort</v-btn>
                    <v-btn color="success" @click="performContinueAll">Continue For All</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-layout>
</template>

<script>

export default {
    props: [
        'plantID'
    ],
    data(){
        return {
            processingFile: false,
            selectedFile: null,
            performingImport: false,

            errorMessagesExpanded: false,
            errorMessages: [],
            continueDialogVisible: false,
            continueQuestion: null,
            continueReference: null,
            continueAllReference: null,
            fileReference: null,
            confirmedContinueReferences: [],

            successMessage: null
        };
    },
    computed: {
        validFileTypes(){
            return [
                "csv"
            ]
        },
        inputFileTypes(){
            return this.validFileTypes.map(x => "." + x).join(", ");
        },
        fileName(){
            if(this.selectedFile == null){
                return "";
            }

            return this.selectedFile.name;
        },
        showWarning(){
            return this.fileExtensionWarning;
        },
        fileExtensionWarning(){
            return this.selectedFile.extension == null 
                || !this.validFileTypes.some(x => x.toLowerCase() === this.selectedFile.extension.toLowerCase());
        },
        hasError(){
            return this.errorMessages.length > 0;
        },
        hasSuccess(){
            return this.successMessage;
        }
    },
    methods: {
        addFile(){
            if(this.processingFile || this.performingImport){
                return;
            }

            this.$refs["newFile"].click();
        },
        getFileData(event) {
            let file = null;

            this.processingFile = true;

            const files = event.target.files;
            if(files[0] !== undefined) {
                if(files[0].name.lastIndexOf('.') > 0) {
                    let fileNameSplit = files[0].name.split('.');
                    let extension = fileNameSplit[fileNameSplit.length - 1];
                    let mimeType = files[0].type;

                    file = {};
                    this.$set(file, "name", files[0].name);
                    this.$set(file, "extension", extension);
                    this.$set(file, "mimeType", mimeType);
                    this.$set(file, "data", files[0]);
                }
            }

            this.processingFile = false;

            return file;
        },
        handleNewFile(event){
            this.selectedFile = this.getFileData(event);
            this.$refs["newFile"].value = null; // Reset selector

            // Reset the form
            this.errorMessagesExpanded = false;
            this.errorMessages.splice(0, this.errorMessages.length);
            this.fileReference = null;
            this.confirmedContinueReferences.splice(0, this.confirmedContinueReferences.length);
            this.successMessage = null;
        },
        removeFile(){
            this.selectedFile = null;
        },
        async performImport(){
            if(this.selectedFile == null){
                return;
            }

            this.performingImport = true;
            this.uploadActuals();
        },
        performAbort(){
            this.continueDialogVisible = false;
            
            this.showErrors([{ description: `User aborted the process (${this.continueQuestion})` }]);
        },
        performContinue(){
            this.continueDialogVisible = false;

            // Add this continue to our list of accepted continues
            this.confirmedContinueReferences.push(this.continueReference);
            this.uploadActuals();
        },
        performContinueAll() {
            this.continueDialogVisible = false;
            // Add this continue to our list of accepted continues
            this.confirmedContinueReferences.push(this.continueAllReference);
            this.uploadActuals();
        },
        async uploadActuals(){
            try {
                let response = await this.$store.dispatch("DataImportStore/uploadActuals", {
                    PlantID: this.plantID,
                    FileData: this.selectedFile.data,
                    ConfirmedContinueReferences: this.confirmedContinueReferences,
                    FileReference: this.fileReference
                });

                var errors = response.RowResponses
                    .filter(x => x.ErrorDescription != null || x.ErrorDetail != null)
                    .map(x => {
                        return {
                            rowIndex: x.RowIndex,
                            description: x.ErrorDescription,
                            detail: x.ErrorDetail
                        };
                    });

                var continues = response.RowResponses
                    .filter(x => x.ContinueReference != null && x.ContinueAllReference != null)
                    .map(x => {
                        return {
                            rowIndex: x.RowIndex,
                            question: x.ContinueQuestion,
                            reference: x.ContinueReference,
                            continueAllReference: x.ContinueAllReference
                        };
                    });

                if(errors.length > 0){
                    this.showErrors(errors.map(x => {
                        return {
                            description: `Row ${(x.rowIndex + 1)}/${response.TotalRows}: ${x.description}`,
                            detail: x.detail
                        };
                    }));
                } else if(continues.length > 0){
                    this.askContinue(
                        `Row ${(continues[0].rowIndex + 1)}/${response.TotalRows}: ${continues[0].question}`,
                        continues[0].reference,
                        response.FileReference,
                        continues[0].continueAllReference
                    );
                } else {
                    this.showSuccess(`All ${response.TotalRows}/${response.TotalRows} rows were successfully processed.`);
                }
            } catch(err){
                if(err.response && err.response.data && err.response.data.ExceptionMessage){
                    this.showErrors([{ description: err.response.data.ExceptionMessage }]);
                } else {
                    this.showErrors([{ description: err.message, detail: err }]);
                }
            }
        },
        showErrors(errorMessages){
            errorMessages.forEach(x => {
                x.showDetail = false;
            });
            this.errorMessages.splice(0, this.errorMessages.length, ...errorMessages);
            this.errorMessagesExpanded = true;

            this.selectedFile = null;
            this.performingImport = false;
        },
        showDetail(errorMessage){
            errorMessage.showDetail = true;
        },
        hideDetail(errorMessage){
            errorMessage.showDetail = false;
        },
        askContinue(question, reference, fileReference, continueAllReference){
            this.continueDialogVisible = true;
            this.continueQuestion = question;
            this.continueReference = reference;
            this.fileReference = fileReference;
            this.continueAllReference = continueAllReference;
        },
        showSuccess(message){
            this.successMessage = message;
            
            this.selectedFile = null;
            this.performingImport = false;
        }
    }
}
</script>
<style scope>
.clickable {
    cursor: pointer;
}

.stacktrace {
    word-break: break-all;
    white-space: pre-line;
}
</style>